import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, Link } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import MobileBottomNav from "@/components/MobileBottomNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import { PreviewProvider, PreviewBanner } from "@/components/PreviewMode";
import { useAuth } from "@/lib/auth";

import Landing from "@/pages/Landing";
import Explore from "@/pages/Explore";
import AIConcierge from "@/pages/AIConcierge";
import ExperienceDetail from "@/pages/ExperienceDetail";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import CustomerDashboard from "@/pages/CustomerDashboard";
import ProviderDashboard from "@/pages/ProviderDashboard";
import HostDashboard from "@/pages/HostDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import PropertyConcierge from "@/pages/PropertyConcierge";
import Messages from "@/pages/Messages";
import HowItWorks from "@/pages/HowItWorks";
import About from "@/pages/About";
import BecomeProvider from "@/pages/BecomeProvider";
import BecomeHost from "@/pages/BecomeHost";
import AccountSettings from "@/pages/AccountSettings";
import ForgotPassword from "@/pages/ForgotPassword";
import ConciergeOnboarding from "@/pages/ConciergeOnboarding";
import SafetyCenter from "@/pages/SafetyCenter";
import SharedExperience from "@/pages/SharedExperience";
import ConciergePublic from "@/pages/ConciergePublic";
import AdminInviteAccept from "@/pages/AdminInviteAccept";
import CustomerPlanDetail from "@/pages/CustomerPlanDetail";
import CustomerBriefDetail from "@/pages/CustomerBriefDetail";
import Home from "@/pages/Home";

// Stable role arrays — defined once at module load, never re-created on render
const ROLE_CUSTOMER = ["customer"];
const ROLE_PROVIDER = ["provider"];
const ROLE_HOST = ["host"];
const ROLE_ADMIN = ["admin"];
const HIDE_NAV_PATHS = ["/login", "/signup"];

// 404 — preserves URL in the address bar so refresh stays on the same path (no silent home-redirect)
function NotFound(){
  const loc = useLocation();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FFF8EE] px-6">
      <div className="max-w-md text-center" data-testid="not-found">
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#064E3B]">404 · Not Found</div>
        <h1 className="font-serif text-4xl mt-3 text-[#071A2D]">This page isn&apos;t here.</h1>
        <p className="text-sm text-[#071A2D]/65 mt-3">
          <span className="font-mono px-2 py-0.5 rounded-md bg-white border border-[#071A2D]/10">{loc.pathname}</span> doesn&apos;t match a TREL page.
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/" className="btn-coral">Home</Link>
          <Link to="/explore" className="btn-outline-emerald">Explore</Link>
        </div>
      </div>
    </div>
  );
}

function AppRouter(){
  const loc = useLocation();
  const { user } = useAuth();
  // Synchronous detection for OAuth callback BEFORE other route checks
  if (loc.hash?.includes("session_id=")) return <AuthCallback />;

  const hideNav = HIDE_NAV_PATHS.includes(loc.pathname) || loc.pathname.startsWith("/stay/");
  const isPreview = Boolean(user && user.preview);
  return (
    <>
      <PreviewBanner isPreview={isPreview}/>
      {!hideNav && <Navbar/>}
      <ScrollToTop/>
      <Routes>
        {/* Phase 49 — Minimal premium homepage. The dedicated <Home/>
            component renders the new hero+chips+how+supply layout.
            The full AI Concierge form (city, budget, guests, occasion,
            plan generation) remains at /ai-concierge for the deep flow
            after a user types a request on the homepage. */}
        <Route path="/" element={<Home/>}/>
        <Route path="/welcome" element={<Landing/>}/>
        <Route path="/explore" element={<Explore/>}/>
        <Route path="/ai-concierge" element={<AIConcierge/>}/>
        <Route path="/ai" element={<Navigate to="/" replace/>}/>
        <Route path="/experience/:id" element={<ExperienceDetail/>}/>
        <Route path="/how" element={<HowItWorks/>}/>
        <Route path="/how-it-works" element={<Navigate to="/how" replace/>}/>
        <Route path="/about" element={<About/>}/>
        <Route path="/become-provider" element={<BecomeProvider/>}/>
        <Route path="/become-host" element={<BecomeHost/>}/>
        <Route path="/login" element={<Login/>}/>
        <Route path="/signup" element={<Signup/>}/>
        <Route path="/forgot" element={<ForgotPassword/>}/>
        <Route path="/forgot-password" element={<Navigate to="/forgot" replace/>}/>
        <Route path="/account" element={<ProtectedRoute><AccountSettings/></ProtectedRoute>}/>
        <Route path="/stay/:slug" element={<PropertyConcierge/>}/>
        <Route path="/safety" element={<SafetyCenter/>}/>
        <Route path="/share/:token" element={<SharedExperience/>}/>
        <Route path="/concierge/:slug" element={<ConciergePublic/>}/>
        <Route path="/admin/invite/:token" element={<AdminInviteAccept/>}/>

        <Route path="/customer" element={<ProtectedRoute roles={ROLE_CUSTOMER}><CustomerDashboard/></ProtectedRoute>}/>
        <Route path="/customer/plans/:planId" element={<ProtectedRoute roles={ROLE_CUSTOMER}><CustomerPlanDetail/></ProtectedRoute>}/>
        <Route path="/customer/requests/:briefId" element={<ProtectedRoute roles={ROLE_CUSTOMER}><CustomerBriefDetail/></ProtectedRoute>}/>
        <Route path="/provider" element={<ProtectedRoute roles={ROLE_PROVIDER}><ProviderDashboard/></ProtectedRoute>}/>
        <Route path="/onboarding/concierge" element={<ProtectedRoute roles={ROLE_PROVIDER}><ConciergeOnboarding/></ProtectedRoute>}/>
        <Route path="/host" element={<ProtectedRoute roles={ROLE_HOST}><HostDashboard/></ProtectedRoute>}/>
        <Route path="/admin" element={<ProtectedRoute roles={ROLE_ADMIN}><AdminDashboard/></ProtectedRoute>}/>
        <Route path="/messages" element={<ProtectedRoute><Messages/></ProtectedRoute>}/>

        {/* 404 — preserves URL so refresh stays on the same page (no silent home redirect) */}
        <Route path="*" element={<NotFound/>}/>
      </Routes>
      <MobileBottomNav/>
    </>
  );
}

export default function App(){
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PreviewProvider>
            <ErrorBoundary>
              <AppRouter/>
            </ErrorBoundary>
            <Toaster/>
          </PreviewProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
